<template>
  <div>
    <b-col class="d-flex justify-content-end px-0 pt-25">
      <filter-wrapper
        :properties="selectedFilters"
        :width-offset="370"
        @clicked="openSelectedFilter"
        @cleared="clearFilters"
      />
      <dropdown
        :title="$tk('ClientPortal.MasterTable.Filters')"
        class="mb-1 mr-1"
        :disabled="isLoading"
        :show-dropdown="showFilters"
        @change="toggleFilters"
      >
        <template #menu-items>
          <dropdown-submenu
            v-for="(filter, index) in model.filters"
            :key="filter.name + '-' + index"
            :input-id="filter.name"
            :text="filter.name"
            input-name="submenu"
            :selected-filter="selectedFilter(filter.name)"
            :checked="menu"
            @changeSelectedMenu="changeMenu"
          >
            <template #items>
              <template v-if="filter.name==='Statuses'">
                <dropdown-item
                  v-for="(status, statusIndex) in model.statuses"
                  :key="status.text + '-' + statusIndex"
                  :label="status.text"
                  :filter-name="filter.name"
                  :value="status.value"
                  :menu-name="filter.name"
                  :selected="status.selected"
                  @change="includeOrExclude"
                />
              </template>
              <template v-else-if="filter.name==='Types'">
                <dropdown-item
                  v-for="(type, typeIndex) in model.types"
                  :key="type.text + '-' + typeIndex"
                  :label="type.text"
                  :filter-name="filter.name"
                  :selected="type.selected"
                  :value="type.value"
                  :menu-name="filter.name"
                  @change="includeOrExclude"
                />
              </template>
              <template v-else>
                <dropdown-item>
                  <b-form-input
                    v-model="model.serverParams.columnFilters[filter.name.toLowerCase()]"
                    class="__dropdown-input"
                    :placeholder="filter.name"
                    :disabled="onChange"
                    @input="onInputChange(filter.name)"
                  />
                </dropdown-item>
                <dropdown-item
                  v-for="(option, optionIndex) in filter.operators"
                  :key="option + '-' + optionIndex"
                  type="radio"
                  :label="option"
                  :filter-name="filter.name"
                  :selected="filter.selected"
                  :value="option"
                  :menu-name="filter.name"
                  @change="includeOrExclude"
                />
              </template>
            </template>
            <template #footer>
              <dropdown-clear-item
                :title="$tk('ClientPortal.Filters.Clear')"
                @click="clearFilters(filter.name, false)"
              />
            </template>
          </dropdown-submenu>
          <dropdown-clear-item
            :title="$tk('ClientPortal.Filters.ClearAll')"
            @click="clearFilters"
          />
        </template>
      </dropdown>
      <SearchInputField
        :disabled="isLoading"
        :placeholder="$tk('Admin.Transactions.SearchByAccount')"
        @onInputSearch="onSearchChange"
      />
    </b-col>
    <b-card
      class="my-50 admin-table--management"
      no-body
    >
      <Table
        :data="allTransactions"
        :columns="transactionsColumns"
        remote-mode
        :is-loading="isLoading"
        class="admin-table__data"
        type-of-table="transactions"
        @changePage="changeCurrentPage"
        @onSortChange="sort"
      />
      <b-modal
        id="approve-reject-transactions-modal"
        :title="modalConfirm.type === 2 ? $tk('ClientPortal.Admin.AreYouSureYouWantToApproveTransaction') : $tk('ClientPortal.Admin.AreYouSureYouWantToRejectTransaction')"
        centered
        no-close-on-backdrop
        no-enforce-focus
        footer="footer"
        @show="loadModalConfirm"
      >
        <div>
          {{ $tk("ClientPortal.Admin.Account") }}: <b>{{ modalConfirm.info.account }}</b> <br>
          {{ $tk("ClientPortal.Admin.Amount") }}: <b>{{ modalConfirm.info.amount }}</b> <br>
          {{ $tk("ClientPortal.Admin.Message") }}: <b>{{ modalConfirm.info.message }}</b> <br>
          {{ $tk("ClientPortal.Admin.Type") }}: <b>{{ modalConfirm.info.type }}</b> <br>
        </div>

        <template #modal-footer="{ cancel, close }">
          <b-button
            class="m-0"
            size="sm"
            variant="outline-secondary"
            @click="cancel()"
          >
            {{ $tk('ClientPortal.Form.Actions.Cancel') }}
          </b-button>
          <b-button
            :variant="modalConfirm.type == 3 ? 'danger' : 'primary'"
            class="m-0 ml-1 float-right"
            size="sm"
            :disabled="modalConfirm.loading"
            @click="modalConfirm.type == 2 ? updateTransactionStatus(modalConfirm.info, 'Completed'): updateTransactionStatus(modalConfirm.info, 'Rejected'); close()"
          >
            <span v-if="modalConfirm.type == 2">
              {{ $tk("ClientPortal.Admin.Approve") }}
            </span>
            <span v-else>
              {{ $tk("ClientPortal.Admin.Reject") }}
            </span>
          </b-button>
        </template>
      </b-modal>
    </b-card>
  </div>
</template>

<script>
import Table from '@/components/Table.vue'
import {
  computed, ref, onMounted, reactive,
} from '@vue/composition-api'
import store from '@/store'
import SearchInputField from '@/components/SearchInputField.vue'
import AccountAdminService from '@/api/admin/accountAdmin.service'
import notification from '@/mixins/toastification'
import Dropdown from '@/components/Dropdown.vue'
import DropdownItem from '@/components/DropdownItem.vue'
import DropdownSubmenu from '@/components/DropdownSubmenu.vue'
import DropdownClearItem from '@/components/DropdownClearItem.vue'
import FilterWrapper from '@/components/FilterWrapper.vue'
import translation from '@/mixins/translate'
import onSortChange from '@/mixins/onSortChange'
import { debounce } from 'vue-debounce'

export default {
  components: {
    Table,
    SearchInputField,
    Dropdown,
    DropdownItem,
    DropdownSubmenu,
    FilterWrapper,
    DropdownClearItem,
  },
  mixins: [notification],
  setup() {
    const transactionsColumns = [
      {
        label: translation.methods.$tk('ClientPortal.Admin.Transaction.Id'),
        field: 'id',
        width: '100px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Transaction.Account'),
        field: 'account',
        width: '250px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Transaction.Amount'),
        field: 'amount',
        width: '170px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Transaction.TypeOfTransaction'),
        field: 'type',
        width: '150px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Transaction.Message'),
        field: 'message',
        width: '270px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Transaction.Status'),
        field: 'status',
        width: '150px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Transaction.CreatedAt'),
        field: 'createdAt',
        type: 'date',
        dateInputFormat: 'dd/MM/yyyy HH:mm',
        dateOutputFormat: 'dd/MM/yyyy HH:mm',
        width: '300px',
      },
    ]
    const allTransactions = computed(() => store.getters.allTransactions)
    const model = computed(() => store.getters.getTransactionModel)
    const { pageLength } = allTransactions.value
    const isLoading = ref(false)
    const menu = ref('')
    const showFilters = ref(false)
    const selectedFilters = reactive([])
    const onChange = ref(false)

    onMounted(async () => {
      isLoading.value = true
      await store.dispatch('getTransactions')
      isLoading.value = false
    })

    const changeCurrentPage = async value => {
      model.value.serverParams.page = value
      await store.dispatch('getFilteredTransactions', model.value)
    }

    const sort = value => {
      onSortChange(value, model, 'getFilteredTransactions')
    }

    const modalConfirm = reactive(store.getters.transactionsModalConfirm)

    const loadModalConfirm = () => {
      const data = computed(() => modalConfirm)
      modalConfirm.loading = data.value.loading
      modalConfirm.type = data.value.type
      modalConfirm.info = data.value.info
    }

    const updateTransactionStatus = async (filter, status) => {
      try {
        const response = await AccountAdminService.updateTransactionStatus(filter, (status === 'Completed') ? 2 : 3)
        if (response.saved) {
          notification.methods.success(response.message)
        } else {
          notification.methods.error(response.message)
        }
      } catch (err) {
        notification.methods.error(err.message)
      } finally {
        await store.dispatch('getFilteredTransactions', model.value)
      }
    }

    const toggleFilters = (toogleFilter = null) => {
      menu.value = ''
      showFilters.value = toogleFilter ?? !showFilters.value
    }

    const changeMenu = newOpenedMenu => {
      menu.value === newOpenedMenu ? menu.value = '' : menu.value = newOpenedMenu
    }

    const onFilterChange = async () => {
      onChange.value = true
      await store.dispatch('getFilteredTransactions', model.value)
      onChange.value = false
    }

    const updateSelectedFilters = (filter, count) => {
      const filterObj = selectedFilters.find(f => f.name === filter)
      if (filterObj) {
        if (count > 0) { filterObj.selectedFilters = count } else {
          const filterIndex = selectedFilters.findIndex(f => f.name === filter)
          selectedFilters.splice(filterIndex, 1)
        }
      } else {
        selectedFilters.push({ name: filter, selectedFilters: count })
      }
    }

    const updateStatuses = (filter, value) => {
      const { propertyFilters } = model.value.serverParams
      const selectedFilter = propertyFilters[filter]
      const optionIndex = selectedFilter.value.indexOf(value)
      const statusSelected = model.value.statuses.find(status => status.value === value)
      const { columnFilters } = model.value.serverParams
      if (optionIndex < 0) {
        selectedFilter.value.push(value)
        statusSelected.selected = true
        columnFilters[filter].push(value)
      } else {
        selectedFilter.value.splice(optionIndex, 1)
        statusSelected.selected = false
        columnFilters[filter].splice(optionIndex, 1)
        if (selectedFilter.value.length === 0 && columnFilters[filter].length === 0) {
          delete propertyFilters[filter]
          delete columnFilters[filter]
        }
      }
    }

    const updatePropertyFilter = (filter, value, operator) => {
      const { propertyFilters } = model.value.serverParams
      const selectedFilter = propertyFilters[filter]
      if (selectedFilter.operator === operator) {
        delete propertyFilters[filter]
      } else {
        selectedFilter.operator = operator
        selectedFilter.value = value
      }
    }

    const updatePropertyFilters = (filter, value, operator = null) => {
      const { propertyFilters } = model.value.serverParams
      const { columnFilters } = model.value.serverParams
      const filterObject = filter.toLowerCase()
      if (filterObject in propertyFilters) {
        if (filter !== 'Id') {
          updateStatuses(filterObject, value)
        } else {
          updatePropertyFilter(filterObject, value, operator)
        }
      } else {
        propertyFilters[filterObject] = {
          operator,
          property: filter,
          value: filter === 'Id' ? value : [value],
        }
        if (filter !== 'Id') {
          columnFilters[filterObject] = []
          columnFilters[filterObject].push(value)
        }
      }
      const count = filter === 'Id' ? 1 : propertyFilters[filterObject]?.value.length
      updateSelectedFilters(filter, count)
    }

    const includeOrExclude = (filter, option) => {
      const filterObject = filter.toLowerCase()
      if (filter === 'Id') {
        const selectedFilter = model.value.filters.find(f => f.name === filter)
        const { columnFilters } = model.value.serverParams
        selectedFilter.selected === option ? selectedFilter.selected = '' : selectedFilter.selected = option
        updatePropertyFilters(filter, columnFilters[filter.toLowerCase()], option)
        if (!columnFilters[filter.toLowerCase()]) { return }
        onFilterChange()
      } else {
        const selectedFilter = model.value[filterObject]
        const selectedOption = selectedFilter.find(o => o.value === option)
        selectedOption.selected === true ? selectedOption.selected = false : selectedOption.selected = true
        updatePropertyFilters(filter, option)
        onFilterChange()
      }
    }

    const onSearchChange = async value => {
      model.value.serverParams.account = value
      if (!value) { await store.dispatch('getFilteredTransactions', model.value) }
      await store.dispatch('getFilteredTransactions', model.value)
    }

    const clearFilters = (filter = null, closeAllDropdowns = true) => {
      const { propertyFilters } = model.value.serverParams
      const { columnFilters } = model.value.serverParams
      const { statuses } = model.value
      const { types } = model.value
      const { filters } = model.value
      if (!filter) {
        selectedFilters.splice(0, selectedFilters.length)
        filters.forEach(f => { f.selected = '' })
        Object.keys(propertyFilters).forEach(propertyFilter => { delete propertyFilters[propertyFilter] })
        Object.keys(columnFilters).forEach(columnFilter => { columnFilters[columnFilter] = null })
        statuses.forEach(status => { status.selected = false })
        types.forEach(status => { status.selected = false })
        toggleFilters(false)
      } else {
        const selectedFilter = model.value.filters.find(f => f.name === filter)
        const includedFilterIndex = selectedFilters.findIndex(f => f.name === filter)
        const filterObject = filter.toLowerCase()
        columnFilters[filterObject] = null
        if (includedFilterIndex > -1) { selectedFilters.splice(includedFilterIndex, 1) }
        selectedFilter.selected = ''
        delete propertyFilters[filterObject]
        if (filter !== 'Id') model.value[filterObject].forEach(status => { status.selected = false })
        if (closeAllDropdowns) { toggleFilters(false) }
      }
      onFilterChange()
      menu.value = ''
    }

    const selectedFilter = name => {
      const filter = selectedFilters.findIndex(f => f.name === name)
      if (filter > -1) return true
      return false
    }

    const onInputChange = debounce(filter => {
      const filterObject = filter.toLowerCase()
      const { propertyFilters } = model.value.serverParams
      const { columnFilters } = model.value.serverParams
      if (filterObject in propertyFilters) {
        propertyFilters[filterObject].value = columnFilters[filterObject]
      } else { return }
      onFilterChange()
    }, 400)

    const openSelectedFilter = filterName => {
      toggleFilters(true)
      menu.value = filterName
    }

    window.onclick = e => {
      if (!e.path.find(x => x.className?.includes('dropdown'))) {
        menu.value = ''
        toggleFilters(false)
      }
    }

    return {
      allTransactions,
      isLoading,
      changeCurrentPage,
      pageLength,
      modalConfirm,
      updateTransactionStatus,
      loadModalConfirm,
      model,
      showFilters,
      menu,
      selectedFilters,
      toggleFilters,
      changeMenu,
      includeOrExclude,
      onSearchChange,
      clearFilters,
      transactionsColumns,
      sort,
      selectedFilter,
      openSelectedFilter,
      onInputChange,
      onChange,
    }
  },
}
</script>
